import { useCallback, useEffect, useState } from 'react';

import { useRouter } from 'next/router';

import { useBreakpoint, useProductBrowser } from '@techstyle/react-components';

import usePrevious from '../../utils/usePrevious';
import { usePageNum } from '../GridPageNumProvider/GridPageNumProvider';

import {
  EnabledFilters,
  FilterActionOptions,
  FilterItem,
} from './GridFilterBar';

type UseGridFilterBarUtilsParams = {
  enabledFilters?: EnabledFilters;
  defaultEnabledFilters?: EnabledFilters;
  setEnabledFilters?: (prev: any) => void;
  setDefaultEnabledFilters?: (prev: any) => void;
  filterItems?: FilterItem[];
};

export function useGridFilterBarUtils({
  enabledFilters,
  defaultEnabledFilters,
  setEnabledFilters,
  setDefaultEnabledFilters,
  filterItems,
}: UseGridFilterBarUtilsParams) {
  const router = useRouter();
  const { totalFilterCount, filters, filterSettings, setFilterSettings } =
    useProductBrowser();
  const previousTotalFilterCount = usePrevious(totalFilterCount);
  const { setPageNum } = usePageNum();

  const { isDesktop } = useBreakpoint();
  const showCategoriesInSidebar = !isDesktop && filterItems?.length;

  const updateRoute = useCallback(
    async params => {
      const [currentUrl] = router.asPath.split('#', 1);
      const [currentPathname, currentParams] = currentUrl.split('?');
      const newParams = new URLSearchParams(currentParams);
      const keyToJoin = 'product_category_id_list';

      if (!params || !params[keyToJoin]?.length) {
        newParams.delete(keyToJoin);
      }

      Object.keys(params).forEach(key => {
        if (key === keyToJoin) {
          newParams.set(
            keyToJoin,
            Array.from(new Set(params[keyToJoin])).join('-')
          );
        } else {
          newParams.set(key, params[key]);
        }
      });

      const newQueryString = newParams.toString();
      const asPath = newQueryString
        ? `${currentPathname}?${newQueryString}`
        : `${currentPathname}`;

      await router.replace(
        {
          pathname: router.pathname,
          query: { ...router.query, ...params },
        },
        asPath,
        { shallow: true }
      );
    },
    [router]
  );

  const clearFilterQueryParams = useCallback(async () => {
    setPageNum(1);
    await updateRoute({
      product_category_id_list: [],
      pageNum: 1,
    });
  }, [updateRoute, setPageNum]);

  const updateFilterQueryParams = useCallback(
    async ({ id, isAdd, defaultEnabledFilters = {} }) => {
      const mergedFilters = {
        ...enabledFilters,
        ...defaultEnabledFilters,
      } as EnabledFilters;
      const ids = Object.entries(mergedFilters).reduce((keys, [key, value]) => {
        if (value.filter.type === 'product_category_id_list') {
          keys.push(key);
        }
        return keys;
      }, []);

      const params = {
        product_category_id_list: isAdd
          ? [...ids, id]
          : [...ids.filter(key => key !== id)],
        pageNum: 1,
      };
      await updateRoute(params);
      setPageNum(1);
    },
    [enabledFilters, updateRoute, setPageNum]
  );

  const addFilter = useCallback(
    async (
      { id, filter, label }: FilterActionOptions,
      updateQueryParams = false,
      defaultEnabledFilters = {}
    ) => {
      // when adding a filter for the first time, add all defaultFilters to the enabledFilters
      // then, empty defaultFilters so they're ignored afterwards
      setEnabledFilters(prev => ({
        ...prev,
        ...defaultEnabledFilters,
        [id]: {
          label,
          filter,
        },
      }));
      if (updateQueryParams) {
        await updateFilterQueryParams({
          id,
          isAdd: true,
          defaultEnabledFilters,
        });
      }
      const defaultKeys = Object.keys(defaultEnabledFilters);
      if (defaultKeys.length > 0) {
        setDefaultEnabledFilters(() => ({}));
      }
    },
    [setDefaultEnabledFilters, setEnabledFilters, updateFilterQueryParams]
  );

  const removeFilter = useCallback(
    async (
      id: string,
      updateQueryParams = false,
      defaultEnabledFilters = {}
    ) => {
      setEnabledFilters(prev => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [id]: idToRemove, ...rest } = prev;
        return { ...rest, ...defaultEnabledFilters };
      });
      if (updateQueryParams) {
        await updateFilterQueryParams({
          id,
          isAdd: false,
          defaultEnabledFilters,
        });
      }
      const defaultKeys = Object.keys(defaultEnabledFilters);
      if (defaultKeys.length > 0) {
        setDefaultEnabledFilters(() => ({}));
      }
    },
    [setDefaultEnabledFilters, setEnabledFilters, updateFilterQueryParams]
  );

  const toggleFilter = useCallback(
    ({ id, filter, label }: FilterActionOptions) => {
      setEnabledFilters(prev => {
        if (prev[id]) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { [id]: idToRemove, ...rest } = prev;
          return rest;
        }
        return {
          ...prev,
          [id]: {
            label,
            filter,
          },
        };
      });
    },
    [setEnabledFilters]
  );

  const clearFilters = useCallback(() => {
    setEnabledFilters({});
    setDefaultEnabledFilters({});
    clearFilterQueryParams();
  }, [clearFilterQueryParams, setDefaultEnabledFilters, setEnabledFilters]);

  useEffect(() => {
    if (
      showCategoriesInSidebar &&
      totalFilterCount === 0 &&
      previousTotalFilterCount &&
      previousTotalFilterCount > 0
    ) {
      // When Clearing all filters from the sidebar, unselect all categories from the filter bar.
      clearFilters();
    }
  }, [
    totalFilterCount,
    showCategoriesInSidebar,
    previousTotalFilterCount,
    clearFilters,
  ]);

  const [alreadySetFilters, setAlreadySetFilters] = useState(false);
  useEffect(() => {
    const queryIds = router.query?.product_category_id_list;
    if (
      !alreadySetFilters &&
      filters &&
      queryIds &&
      showCategoriesInSidebar !== undefined
    ) {
      // since we really want to do this once on startup
      setAlreadySetFilters(true);
      const categoryIds = Array.isArray(queryIds)
        ? [...queryIds]
        : (router.query?.product_category_id_list as string)
            ?.split('-')
            .filter(id => !!id);
      // parse the category ids from the query params
      // and select them in the filter bar as well
      const defaultEnabledFilters = categoryIds.reduce(
        (acc, categoryId) => {
          const filterItem = filterItems.find(item => item.id === categoryId);
          if (filterItem) {
            const { id, filter, label } = filterItem;
            // join in a single id if there's multiple ids
            if (
              filter?.aggregationFilter?.product_category_id_list?.length > 0
            ) {
              filter.aggregationFilter.product_category_id_list = [
                filter.aggregationFilter.product_category_id_list.join(','),
              ];
            }

            acc[id] = { label, filter };
          }
          return acc;
        },
        { ...enabledFilters }
      );

      if (
        showCategoriesInSidebar &&
        categoryIds.find(categoryId => categoryId.includes(','))
      ) {
        // workaround for slides with multiple IDs.
        // Without this, they would show up as "1000, 1001"
        // (separate values) instead of a single filter in the filter drawer.
        // The issue is that this triggers a separate products request,
        // since we're updating the enabledFilters directly.
        setTimeout(() =>
          setEnabledFilters(prev => ({ ...prev, ...defaultEnabledFilters }))
        );
      } else {
        setDefaultEnabledFilters(defaultEnabledFilters);
      }
    }
  }, [
    addFilter,
    alreadySetFilters,
    enabledFilters,
    filterItems,
    router,
    router.query,
    updateRoute,
    filters,
    setDefaultEnabledFilters,
    showCategoriesInSidebar,
    setEnabledFilters,
  ]);

  useEffect(() => {
    if (showCategoriesInSidebar) {
      // add filter items both on the sidebar and the filterbar
      const categoryFilterItems = [];
      const categorySettings = filterSettings.find(
        filter => filter.field === 'product_category_id_list'
      );

      filterItems
        // only add 'product_category_id_list' aggregations into "Categories" filters section in the sidebar
        .filter(
          item => !!item.filter?.aggregationFilter?.product_category_id_list
        )
        .forEach(filterItem => {
          const { id, filter, label } = filterItem;
          categoryFilterItems.push({
            // Needed for multi-id filters, such as "STRAPLESS & PUSH-UP",
            // which would add 2 filters to the filter list
            value: filter.aggregationFilter.product_category_id_list.join(','),
            label,
            id,
            // needed to show, for example, "T-Shirt" instead of "1582" on the FilterTags
            showLabelAsTag: true,
            addFilterCallback: async () => {
              await addFilter(
                { id, filter, label },
                true,
                defaultEnabledFilters
              );
            },
            removeFilterCallback: async () => {
              await removeFilter(id, true, defaultEnabledFilters);
            },
          });
        });

      if (categorySettings) {
        categorySettings.items = [...categoryFilterItems];
      } else {
        const categoryIdListSettings = {
          field: 'product_category_id_list',
          items: categoryFilterItems,
        };
        setFilterSettings([categoryIdListSettings, ...filterSettings]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterItems, showCategoriesInSidebar]);

  return {
    addFilter,
    removeFilter,
    toggleFilter,
    clearFilters,
  };
}
